/**
 * Page Content to Markdown Service
 *
 * Combines page content extraction with HTML to Markdown conversion
 * to provide structured page content for AI context enrichment.
 */

import {
  extractPageContent,
  extractPageMetadata,
} from "./pageContentExtractor";
import type { ExtractorOptions, PageMetadata } from "./pageContentExtractor";
import { htmlToMarkdown } from "./htmlToMarkdown";
import type { HtmlToMarkdownOptions } from "./htmlToMarkdown";

/**
 * Options for getting page content as Markdown
 */
export interface GetPageContentOptions {
  /** Extractor options */
  extractor?: ExtractorOptions;
  /** HTML to Markdown conversion options */
  markdown?: HtmlToMarkdownOptions;
  /** Include page metadata header (default: true) */
  includeMetadata?: boolean;
  /** Include a separator between metadata and content (default: true) */
  includeSeparator?: boolean;
}

/**
 * Result of page content extraction
 */
export interface PageContentResult {
  /** The page content converted to Markdown */
  markdown: string;
  /** Page metadata */
  metadata: PageMetadata;
  /** Whether the content was truncated */
  truncated: boolean;
  /** Content statistics */
  stats: {
    originalHtmlLength: number;
    markdownLength: number;
    extractionTimeMs: number;
  };
}

/**
 * Format page metadata as a Markdown header
 */
function formatMetadataHeader(metadata: PageMetadata): string {
  const lines: string[] = [];

  // Title as main heading
  if (metadata.title) {
    lines.push(`# ${metadata.title}`);
  }

  // Description as lead paragraph
  if (metadata.description) {
    lines.push("");
    lines.push(`> ${metadata.description}`);
  }

  // Meta information
  const metaItems: string[] = [];

  if (metadata.url) {
    metaItems.push(`**URL:** ${metadata.url}`);
  }

  if (metadata.author) {
    metaItems.push(`**Author:** ${metadata.author}`);
  }

  if (metadata.publishedDate) {
    metaItems.push(`**Published:** ${metadata.publishedDate}`);
  }

  if (metadata.language && metadata.language !== "en") {
    metaItems.push(`**Language:** ${metadata.language}`);
  }

  if (metaItems.length > 0) {
    lines.push("");
    lines.push(metaItems.join(" | "));
  }

  return lines.join("\n");
}

/**
 * Get the current page content as Markdown
 *
 * This is the main function to use for extracting page content
 * for AI context enrichment.
 *
 * @example
 * ```typescript
 * const result = getPageContentAsMarkdown();
 * console.log(result.markdown);
 * // # Page Title
 * // > Page description
 * // ---
 * // ## Section 1
 * // Content...
 * ```
 */
export function getPageContentAsMarkdown(
  options: GetPageContentOptions = {}
): PageContentResult {
  const {
    extractor = {},
    markdown = {},
    includeMetadata = true,
    includeSeparator = true,
  } = options;

  const startTime = performance.now();

  // Extract content from the page
  const extracted = extractPageContent(extractor);

  // Convert HTML to Markdown
  const contentMarkdown = htmlToMarkdown(extracted.html, markdown);

  // Build final markdown
  const parts: string[] = [];

  if (includeMetadata) {
    const header = formatMetadataHeader(extracted.metadata);
    if (header) {
      parts.push(header);
      if (includeSeparator && contentMarkdown) {
        parts.push("\n---\n");
      }
    }
  }

  if (contentMarkdown) {
    parts.push(contentMarkdown);
  }

  // Add truncation notice if content was truncated
  if (extracted.truncated) {
    parts.push("\n\n---\n*[Content truncated due to length]*");
  }

  const finalMarkdown = parts.join("\n").trim();
  const endTime = performance.now();

  return {
    markdown: finalMarkdown,
    metadata: extracted.metadata,
    truncated: extracted.truncated,
    stats: {
      originalHtmlLength: extracted.originalLength,
      markdownLength: finalMarkdown.length,
      extractionTimeMs: Math.round(endTime - startTime),
    },
  };
}

/**
 * Get a simplified version of the page content
 * (just the markdown content without metadata)
 */
export function getSimplePageContent(
  options: Omit<
    GetPageContentOptions,
    "includeMetadata" | "includeSeparator"
  > = {}
): string {
  const result = getPageContentAsMarkdown({
    ...options,
    includeMetadata: false,
    includeSeparator: false,
  });
  return result.markdown;
}

/**
 * Get page content with a maximum character limit
 * Useful for sending to APIs with token limits
 */
export function getPageContentWithLimit(
  maxLength: number,
  options: GetPageContentOptions = {}
): PageContentResult {
  const result = getPageContentAsMarkdown({
    ...options,
    extractor: {
      ...options.extractor,
      maxLength: Math.max(1000, maxLength * 2), // Extract more, then trim
    },
  });

  // If within limit, return as-is
  if (result.markdown.length <= maxLength) {
    return result;
  }

  // Truncate intelligently at paragraph boundaries
  let truncatedMarkdown = result.markdown.substring(0, maxLength);

  // Try to end at a paragraph
  const lastParagraph = truncatedMarkdown.lastIndexOf("\n\n");
  if (lastParagraph > maxLength * 0.8) {
    truncatedMarkdown = truncatedMarkdown.substring(0, lastParagraph);
  }

  // Add truncation notice
  truncatedMarkdown += "\n\n---\n*[Content truncated due to length]*";

  return {
    ...result,
    markdown: truncatedMarkdown,
    truncated: true,
    stats: {
      ...result.stats,
      markdownLength: truncatedMarkdown.length,
    },
  };
}
